import * as React from "react";
import { shouldCommitOnBlur } from "./blur-commit";

export type ParseResult<T> = { ok: true; value: T } | { ok: false };

interface TextInputEditorProps<T> {
  /** Initial draft string seeded into the input. */
  initial: string;
  /**
   * Parse the current draft into the canonical value. Return `{ok:false}`
   * to refuse the commit (editor stays open). Empty-string handling is the
   * parser's responsibility.
   */
  parse: (draft: string) => ParseResult<T>;
  onCommit: (value: T) => void;
  onCancel: () => void;
  /** Native input type. Defaults to "text". */
  inputType?: React.HTMLInputTypeAttribute;
  /** Native inputMode attribute (e.g. "decimal"). */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /** Forwarded native `step` attribute for `<input type="number">`. */
  step?: string;
}

/**
 * Shared scaffold for the four single-`<input>` editors (text, number,
 * currency, date). Owns the draft string, the dedupe ref, the
 * Enter/Escape/blur keybindings, and the canonical input className.
 *
 * Per-type behavior lives entirely in the `parse` callback:
 *   - text: passthrough string (empty → null at the consumer)
 *   - number: `Number(trim)` with NaN guard
 *   - currency: `fromCurrencyInputString` with code-pinning
 *   - date: passthrough string after UTC normalization
 *
 * Returning `{ok:false}` from `parse` refuses the commit and keeps the
 * editor open — same contract the previous hand-rolled commit() functions
 * implemented.
 */
export function TextInputEditor<T>({
  initial,
  parse,
  onCommit,
  onCancel,
  inputType = "text",
  inputMode,
  step,
}: TextInputEditorProps<T>) {
  const [draft, setDraft] = React.useState<string>(initial);
  const committedRef = React.useRef(false);

  const commit = React.useCallback(() => {
    if (committedRef.current) return;
    const result = parse(draft);
    if (!result.ok) return;
    committedRef.current = true;
    onCommit(result.value);
  }, [draft, parse, onCommit]);

  const cancel = React.useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCancel();
  }, [onCancel]);

  return (
    <input
      data-1p-ignore
      type={inputType}
      autoFocus
      className="block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      onBlur={(event) => {
        if (shouldCommitOnBlur(event)) commit();
      }}
      {...(inputMode ? { inputMode } : {})}
      {...(step ? { step } : {})}
    />
  );
}
